import type { TAvailableTag, TError } from '@onesource/schemas';
import { useQuery } from '@tanstack/react-query';

import axios from '../../axios';
import { ENTITY } from '../entity';

/**
 * Prefix key shared by every available-tags variant of a space (both `usedOnly`
 * values). Use this to invalidate the composer's full list AND the filter's
 * narrowed list in one call — React Query matches queries by key prefix.
 */
export const getAvailableTagsBaseQueryKey = (parentSpaceId: string) => [
  ENTITY,
  'spaces',
  parentSpaceId,
  'available-tags',
];

/**
 * Query key for fetching the tags available to a post in a parent space - non-hook version.
 * `usedOnly` is part of the key so the filter's narrowed list and the composer's full
 * list are cached separately and never overwrite each other.
 */
export const getAvailableTagsQueryKey = (parentSpaceId: string, usedOnly = false) => [
  ...getAvailableTagsBaseQueryKey(parentSpaceId),
  { usedOnly },
];

/**
 * Query key for fetching available tags
 */
export const useGetAvailableTagsQueryKey = (parentSpaceId: string, usedOnly = false) =>
  getAvailableTagsQueryKey(parentSpaceId, usedOnly);

interface UseGetAvailableTagsOptions {
  enabled?: boolean;
  /** Return only tags at least one existing post uses — for feed *filter* surfaces. */
  usedOnly?: boolean;
}

/**
 * Hook to fetch the tags a member can attach to a post: curated question tags +
 * content sub-spaces of the parent space + their lessons. Each tag carries a
 * `type` (question | subspace | lesson) for grouping in the selector.
 *
 * @param parentSpaceId - Parent (container) space ID (UUID)
 * @param options - Query options (enabled, etc.) — pass `{ enabled: false }` to fetch lazily
 */
export const useGetAvailableTags = (parentSpaceId: string, options?: UseGetAvailableTagsOptions) => {
  const enabled = options?.enabled ?? true;
  const usedOnly = options?.usedOnly ?? false;

  return useQuery<TAvailableTag[], TError>({
    queryKey: useGetAvailableTagsQueryKey(parentSpaceId, usedOnly),
    queryFn: async () => {
      const res = await axios.get<TAvailableTag[]>(`/community-hub/spaces/${parentSpaceId}/tags/available`, {
        params: usedOnly ? { usedOnly: true } : undefined,
      });
      return res.data;
    },
    enabled: !!parentSpaceId && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
